import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import {
    Activity,
    CheckCircle,
    AlertTriangle,
    Clock,
    TrendingUp,
    TrendingDown,
    ArrowRight,
    Sparkles,
    Upload,
    Play,
    Smartphone,
    FileText,
    ChevronRight,
    MessageCircleIcon
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import TimeAgo from '../components/TimeAgo';
import './Dashboard.css';

function Dashboard() {
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [goalText, setGoalText] = useState('');
    const [recentActivity, setRecentActivity] = useState([]);
    const [stats, setStats] = useState({
        totalTests: 0,
        passRate: 0,
        criticalBugs: 0,
        quotaUsed: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, [currentWorkspace]);

    const loadDashboardData = async () => {
        try {
            // Load recent executions
            const execResponse = await api.get('/tests/executions', {
                params: { limit: 5 }
            });
            setRecentActivity(execResponse.data?.executions || []);

            // Mock stats for now - these should come from an API
            setStats({
                totalTests: 1240,
                passRate: 88,
                criticalBugs: 12,
                quotaUsed: 45
            });
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateScripts = () => {
        if (goalText.trim()) {
            navigate('/test-goals', { state: { goalText } });
        } else {
            navigate('/test-goals');
        }
    };

    const chartData = [
        { day: 'Mon', tests: 45 },
        { day: 'Tue', tests: 52 },
        { day: 'Wed', tests: 38 },
        { day: 'Thu', tests: 65 },
        { day: 'Fri', tests: 48 },
        { day: 'Sat', tests: 25 },
        { day: 'Sun', tests: 35 }
    ];

    const statsCards = [
        {
            icon: Activity,
            label: 'Total Tests Run',
            value: stats.totalTests.toLocaleString(),
            trend: '+12%',
            trendUp: true,
            color: 'primary'
        },
        {
            icon: CheckCircle,
            label: 'Pass Rate',
            value: `${stats.passRate}%`,
            trend: '+2%',
            trendUp: true,
            color: 'success'
        },
        {
            icon: AlertTriangle,
            label: 'Critical Bugs',
            value: stats.criticalBugs,
            trend: '-5%',
            trendUp: false,
            color: 'warning'
        },
        {
            icon: Clock,
            label: 'Quota Used',
            value: `${stats.quotaUsed}%`,
            subtext: '450/1000 mins',
            color: 'purple',
            showProgress: true
        }
    ];

    const getStatusBadge = (status) => {
        switch (status) {
            case 'success':
                return <span className="status-badge success"><CheckCircle size={12} /> Pass</span>;
            case 'failed':
            case 'error':
                return <span className="status-badge error"><AlertTriangle size={12} /> Fail</span>;
            case 'inconclusive':
                return <span className="status-badge inconclusive"><MessageCircleIcon size={12} /> Inconclusive</span>;
            case 'running':
                return <span className="status-badge running"><Play size={12} /> Running</span>;
            default:
                return <span className="status-badge pending"><Clock size={12} /> Pending</span>;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Dashboard</h1>
                    <p className="page-subtitle">Overview of your latest autonomous test runs and health.</p>
                </div>
                <Link to="/apps" className="btn btn-primary">
                    <Upload size={16} />
                    Upload New App
                </Link>
            </div>

            {/* Test Goal Input */}
            <div className="goal-input-card card">
                <div className="goal-input-icon">
                    <Sparkles size={20} />
                </div>
                {/* <div className="goal-input-content">
                    <label className="goal-label">Create New Test Goal</label>
                    <input
                        type="text"
                        className="goal-input"
                        value={goalText}
                        onChange={(e) => setGoalText(e.target.value)}
                        placeholder="Describe your test goal in plain English (e.g., 'Verify user can update profile picture and save')..."
                    />
                </div> */}
                <button className="btn btn-primary" onClick={handleGenerateScripts}>
                    <Sparkles size={16} />
                    Run a new goal
                </button>
            </div>

            {/* Stats Cards */}
            {/* <div className="stats-grid">
                {statsCards.map((stat, index) => (
                    <div key={index} className={`stat-card ${stat.color}`}>
                        <div className="stat-header">
                            <div className={`stat-icon ${stat.color}`}>
                                <stat.icon size={20} />
                            </div>
                            {stat.trend && (
                                <div className={`stat-trend ${stat.trendUp ? 'up' : 'down'}`}>
                                    {stat.trendUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                    {stat.trend}
                                </div>
                            )}
                            {stat.subtext && (
                                <span className="stat-subtext">{stat.subtext}</span>
                            )}
                        </div>
                        <div className="stat-label">{stat.label}</div>
                        <div className="stat-value">{stat.value}</div>
                        {stat.showProgress && (
                            <div className="stat-progress">
                                <div
                                    className="stat-progress-fill"
                                    style={{ width: `${stats.quotaUsed}%` }}
                                ></div>
                            </div>
                        )}
                    </div>
                ))}
            </div> */}

            {/* Main Content Grid */}
            <div className="dashboard-grid">
                {/* Recent Activity */}
                <div className="activity-section card">
                    <div className="card-header">
                        <h3 className="card-title">Recent Activity</h3>
                        <a href="/executions" className="view-all-link">
                            View All <ArrowRight size={14} />
                        </a>
                    </div>
                    <table className="table activity-table">
                        <thead>
                            <tr>
                                <th>STATUS</th>
                                <th>GOAL</th>
                                <th>PLATFORM</th>
                                <th>TIME</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentActivity.length > 0 ? (
                                recentActivity.map((activity, index) => (
                                    <tr key={index}>
                                        <td>{getStatusBadge(activity.status)}</td>
                                        <td>
                                            <Link
                                                to={`/test/execution/${activity.id}`}
                                                className="activity-row-link"
                                            >
                                                {activity.goalText}
                                            </Link>

                                        </td>
                                        <td><span className="device-badge"><Smartphone size={12} /> {activity.platform}</span></td>
                                        <td><TimeAgo date={activity.createdAt} /></td>
                                        {/* <td colSpan="5" style={{ padding: 0 }}>
                                            <Link
                                                to={`/test/execution/${activity.id}`}
                                                className="activity-row-link"
                                            >
                                                <div className="activity-row-content">
                                                    <div className="activity-col">{getStatusBadge(activity.status)}</div>
                                                    <div className="activity-col">
                                                        <div className="activity-name">{activity.testGoal?.goalText?.slice(0, 30) || 'Test Run'}</div>
                                                    </div>
                                                    <div className="activity-col">
                                                        <span className="device-badge">
                                                            <Smartphone size={12} />
                                                            {activity.testGoal?.platform || 'Android'}
                                                        </span>
                                                    </div>
                                                    <div className="activity-col">
                                                        <TimeAgo date={activity.createdAt} />
                                                    </div>
                                                    <div className="activity-col">
                                                        <ChevronRight className="row-arrow" size={16} />
                                                    </div>
                                                </div>
                                            </Link>
                                        </td> */}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        No recent test runs. Create a test goal to get started!
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Right Sidebar */}
                {/* <div className="sidebar-section">
                    <div className="ai-insight-card card">
                        <div className="insight-header">
                            <Sparkles size={16} className="insight-icon" />
                            <span>AI Insight</span>
                        </div>
                        <p className="insight-text">
                            We detected a potential infinite loop in the <strong>Checkout Flow</strong> on weak network conditions.
                        </p>
                        <div className="insight-evidence">
                            <FileText size={16} />
                            <div>
                                <span className="evidence-label">Evidence</span>
                                <span className="evidence-file">trace_log_404.json</span>
                            </div>
                        </div>
                        <a href="#" className="insight-link">View Full Analysis</a>
                    </div>

                    
                    <div className="chart-card card">
                        <h3 className="card-title">Daily Execution Volume</h3>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={150}>
                                <BarChart data={chartData}>
                                    <XAxis
                                        dataKey="day"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                    />
                                    <YAxis hide />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--bg-tertiary)',
                                            border: '1px solid var(--border)',
                                            borderRadius: '8px',
                                            color: 'var(--text-primary)'
                                        }}
                                    />
                                    <Bar
                                        dataKey="tests"
                                        fill="var(--primary)"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div> */}
            </div>
        </div>
    );
}

export default Dashboard;
